import { rem } from 'polished';
import styled, { css } from 'styled-components';

import { grayscale } from '@vimeo/iris/color';
import { DismissX, ChevronRight as NextIcon } from '@vimeo/iris/icons';

import {
  ACTION_ARROW_Z_INDEX,
  ACTIVE_PLAYER_Z_INDEX,
  TITLE_CARD_Z_INDEX,
} from '../../utils/constants';

const PreviousIcon = styled(NextIcon)`
  transform: rotate(180deg);
`;

// Bottom padding is applied when viewed by the owner so the
// customization footer isn't covered up
const bottomPadding = css`
  padding-bottom: ${rem('72px')};
`;

const emptyPlayerLayout = css`
  background-color: ${grayscale(800)};
`;

export const PlayerLayoutSection = styled.section<{
  isEmpty: boolean;
  padBottom: boolean;
}>`
  ${({ isEmpty }) => isEmpty && emptyPlayerLayout}
  ${({ padBottom }) => padBottom && bottomPadding}
`;

const activePlayerStyle = css`
  z-index: ${ACTIVE_PLAYER_Z_INDEX};
  height: 65vh;

  @media only screen and (max-width: 960px) {
    height: 50vh;
  }

  @media only screen and (max-width: 767px) {
    height: 40vh;
  }
`;

const playerIsLoadingStyle = css`
  display: none;
`;

function getSearchHeight({ isSearching = null }: { isSearching: boolean | null }) {
  return css`
    min-height: ${isSearching ? 'none' : '40vh'};
  `;
}

export const FeaturedPlayer = styled.div<{
  isPlayerActive: boolean;
  isLoadingPlayerIframe: boolean;
  isSearching: boolean;
}>`
  width: 100%;
  ${getSearchHeight}
  display: flex;
  justify-content: center;
  overflow: hidden;
  position: relative;
  background-color: black;
  transition: min-height 0.25s linear;

  ${(props) => (props.isPlayerActive ? activePlayerStyle : null)};

  iframe {
    ${(props) => (props.isLoadingPlayerIframe ? playerIsLoadingStyle : null)};
    border: none;
    height: 100%;
    width: 100%;
  }
`;

const sharedActionStyle = css`
  @media only screen and (max-width: 480px) {
    width: 10vw;
  }
`;

const sharedIconStyle = css`
  @media only screen and (max-width: 480px) {
    width: 24px;
  }
`;

const actionArrowStyle = css`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 5vw;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${ACTION_ARROW_Z_INDEX};
  opacity: 0.25;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const actionArrowIconStyle = css`
  width: 40px;
  path {
    fill: white;
  }
`;

export const PreviousIconStyled = styled(PreviousIcon)`
  ${actionArrowIconStyle}
  ${sharedIconStyle};
`;

export const PreviousButton = styled.div`
  ${actionArrowStyle};
  ${sharedActionStyle};
  left: 0;
`;

export const NextIconStyled = styled(NextIcon)`
  ${actionArrowIconStyle};
  ${sharedIconStyle};
  margin-top: -55px;
`;

// Account for space occupied by X button to avoid intersection
export const NextButton = styled.div`
  ${actionArrowStyle};
  ${sharedActionStyle};
  top: 55px;
  right: 0;
`;

export const CloseButton = styled.div`
  position: absolute;
  top: 2%;
  right: 0;
  width: 5vw;
  z-index: ${ACTION_ARROW_Z_INDEX};
  opacity: 0.25;
  cursor: pointer;
  text-align: center;

  &:hover {
    opacity: 1;
  }
`;

export const CloseIconStyled = styled(DismissX)`
  width: 32px;
  path {
    fill: white;
  }
  ${sharedIconStyle};
`;

const windowWidth = '100vw';
const playerElementWidth = '90vw';
const playerElementWidthMobile = '80vw';

export const PlayerElement = styled.div`
  position: absolute;
  width: ${playerElementWidth};
  height: 100%;
  left: calc((${windowWidth} - ${playerElementWidth}) / 2);
  @media only screen and (max-width: 480px) {
    width: ${playerElementWidthMobile};
    left: calc((${windowWidth} - ${playerElementWidthMobile}) / 2);
  }
`;

function getSearchDisplay({ isSearching = null }: { isSearching: boolean | null }) {
  return css`
    display: ${isSearching ? 'none' : 'inherit'};
  `;
}
export const TitleCard = styled.div<{ thumbnail: string; isSearching: boolean }>`
  z-index: ${TITLE_CARD_Z_INDEX};
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  margin: auto;
  ${getSearchDisplay}
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
  background-size: cover;
  background-image: linear-gradient(
      rgba(0, 0, 0, 0.25) 0%,
      rgba(0, 0, 0, 0.25) 50%,
      rgba(0, 0, 0, 0.25) 100%
    ),
    url(${(props) => props.thumbnail || ''});
`;
